import { getTrackReferenceId } from '@livekit/components-core';
import {Track, TrackEvent} from 'livekit-client';
import * as React from 'react';
import {
    AudioTrack,
    TrackReference,
    useLocalParticipant,
    useTracks
} from "@livekit/components-react";
import {useEffect, useRef, useState} from "react";

/** @public */
export interface RoomAudioRendererProps {
    /** Sets the volume for all audio tracks rendered by this component. By default, the range is between `0.0` and `1.0`. */
    volume?: number;
    /**
     * If set to `true`, mutes all audio tracks rendered by the component.
     * @remarks
     * If set to `true`, the server will stop sending audio track data to the client.
     * @alpha
     */
    muted?: boolean;
}

/**
 * The `RoomAudioRenderer` component is a drop-in solution for adding audio to your LiveKit app.
 * It takes care of handling remote participants’ audio tracks and makes sure that microphones and screen share are audible.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <RoomAudioRenderer />
 * </LiveKitRoom>
 * ```
 * @public
 */
export default function CustomRoomAudioRenderer({ volume, muted }: RoomAudioRendererProps) {

    const [blinkIntervalId, setBlinkIntervalId] = useState<number>()
    const localMusic = useRef<TrackReference>()
    const localParticipant = useLocalParticipant()

    const remoteTracks = useTracks(
        [Track.Source.Microphone, Track.Source.ScreenShareAudio, Track.Source.Unknown],
        {
            onlySubscribed: true,
        },
    ).filter((ref) => !ref.participant.isLocal && ref.publication.kind === Track.Kind.Audio)

    const localBackgroundMusicTracks = useTracks(
        [Track.Source.ScreenShareAudio],
        {
            onlySubscribed: false,
        },
    ).filter((ref) => ref.participant.isLocal && ref.publication.kind === Track.Kind.Audio)

    if (localBackgroundMusicTracks.length > 0) {
        localMusic.current = localBackgroundMusicTracks[0] as TrackReference
        const htmlAudio = localMusic.current.publication.audioTrack.attach() as HTMLAudioElement
        htmlAudio.volume = 0.2
    }

    function onSilence() {
        const el = document.querySelector(".lk-control-bar > .lk-button-group") as HTMLDivElement
        const el1 = document.querySelector(".lk-control-bar > .lk-button-group > .lk-button") as HTMLDivElement
        const el2 = document.querySelector(".lk-control-bar > .lk-button-group > .lk-button-group-menu > button") as HTMLDivElement

        const id = setInterval(() => {
            el1.style.color = el1.style.color === "red" ? "black" : "red"
            el2.style.color = el2.style.color === "red" ? "black" : "red"
            el.style.opacity = el.style.opacity === "1" ? "unset" : "1"
        }, 500)
        el.style.opacity = "1"
        setBlinkIntervalId(id)
    }

    function onChange() {
        const el = document.querySelector(".lk-control-bar > .lk-button-group") as HTMLDivElement
        const el1 = document.querySelector(".lk-control-bar > .lk-button-group > .lk-button") as HTMLDivElement
        const el2 = document.querySelector(".lk-control-bar > .lk-button-group > .lk-button-group-menu > button") as HTMLDivElement
        el1.style.color = "white"
        el2.style.color = "white"
        clearInterval(blinkIntervalId)
        console.log("change")

        const pub = localParticipant.localParticipant.getTrackPublication(Track.Source.Microphone)
        console.log(pub?.audioTrack?.listeners(TrackEvent.Restarted))
    }

    useEffect(() => {
        // handle silence detected on local audio track
        const pub = localParticipant.localParticipant.getTrackPublication(Track.Source.Microphone)
        pub?.audioTrack?.removeAllListeners(TrackEvent.AudioSilenceDetected)
        pub?.audioTrack?.on(TrackEvent.AudioSilenceDetected, onSilence)


        pub?.audioTrack?.removeListener(TrackEvent.Restarted, onChange)
        pub?.audioTrack?.on(TrackEvent.Restarted, onChange)

        return () => {
            // remove added listeners
            pub?.audioTrack?.removeListener(TrackEvent.Restarted, onChange)
            pub?.audioTrack?.removeListener(TrackEvent.AudioSilenceDetected, onSilence)
        }

    }, [localParticipant])


    return (
        <div style={{ display: 'none', width: "300px"}}>
            {remoteTracks.map((trackRef) => {

                return <AudioTrack
                    style={{display: "block"}}
                    preload={"auto"}
                    key={getTrackReferenceId(trackRef)}
                    trackRef={trackRef}
                    volume={volume}
                    muted={muted}
                />
                }

            )}

        </div>
    );
}
